import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Form, Input, Button } from 'semantic-ui-react';
import { trackEvent } from '../analytics';
// Helper function to generate key from label
const generateKeyFromLabel = (label) => {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and dashes
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .replace(/-+/g, '-') // Replace multiple dashes with single dash
        .replace(/^-|-$/g, ''); // Remove leading/trailing dashes
};
const AddFormModal = ({ open, onClose, onSave, title = 'Add New Form', saveLabel = 'Save', initialForm, initialPrompt: initialPromptProp }) => {
    const [newForm, setNewForm] = useState({ formKey: '', label: '' });
    const [promptText, setPromptText] = useState(initialPromptProp || '');
    const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (!open)
            return;
        const initialLabel = String(initialForm?.label || '').trim();
        const initialKey = String(initialForm?.formKey || '').trim();
        setNewForm({
            formKey: initialKey || (initialLabel ? generateKeyFromLabel(initialLabel) : ''),
            label: initialLabel,
        });
        setKeyManuallyEdited(!!initialKey);
        setPromptText(initialPromptProp || '');
        setErrors({});
    }, [open, initialForm?.label, initialForm?.formKey, initialPromptProp]);
    const handleLabelChange = (e) => {
        const label = e.target.value;
        setNewForm(prev => ({
            ...prev,
            label,
            // Auto-generate key only if it hasn't been manually edited
            formKey: keyManuallyEdited ? generateKeyFromLabel(prev.formKey) : generateKeyFromLabel(label)
        }));
        // Clear label error when user starts typing
        if (errors.label) {
            setErrors(prev => ({ ...prev, label: undefined }));
        }
    };
    const handleKeyChange = (e) => {
        const formKey = e.target.value;
        setNewForm(prev => ({ ...prev, formKey: generateKeyFromLabel(formKey) }));
        setKeyManuallyEdited(true);
        // Clear key error when user starts typing
        if (errors.formKey) {
            setErrors(prev => ({ ...prev, formKey: undefined }));
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!newForm.label.trim()) {
            newErrors.label = 'Label is required';
        }
        if (!newForm.formKey.trim()) {
            newErrors.formKey = 'Key is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }
        try {
            trackEvent('save_form', { form_key: newForm.formKey });
            await onSave(newForm, promptText.trim() || undefined);
            // Reset form state
            setNewForm({ formKey: '', label: '' });
            setPromptText('');
            setKeyManuallyEdited(false);
            setErrors({});
            onClose();
        }
        catch (error) {
            // Show duplicate-key errors under the Key field
            // @TODO: FLU-193 Handle this.
            if (axios.isAxiosError(error)) {
                const status = error.response?.status;
                const data = error.response?.data;
                if (status === 409) {
                    setErrors(prev => ({
                        ...prev,
                        formKey: data?.error || 'A form with this key already exists. Please choose a different key.',
                    }));
                    return;
                }
            }
            console.error('Error saving form:', error);
        }
    };
    const handleClose = () => {
        setNewForm({ formKey: '', label: '' });
        setPromptText('');
        setKeyManuallyEdited(false);
        setErrors({});
        onClose();
    };
    return (React.createElement(Modal, { open: open, onClose: handleClose },
        React.createElement(Modal.Header, null, title),
        React.createElement(Modal.Content, null,
            React.createElement(Form, null,
                React.createElement(Form.Field, { error: !!errors.label },
                    React.createElement("label", null,
                        "Label ",
                        React.createElement("span", { style: { color: 'red' } }, "*")),
                    React.createElement(Input, { placeholder: 'Enter form label', value: newForm.label, onChange: handleLabelChange }),
                    errors.label && React.createElement("div", { style: { color: 'red', fontSize: '0.9em', marginTop: '0.25em' } }, errors.label)),
                React.createElement(Form.Field, { error: !!errors.formKey },
                    React.createElement("label", null,
                        "Key ",
                        React.createElement("span", { style: { color: 'red' } }, "*")),
                    React.createElement(Input, { placeholder: 'Form key (auto-generated from label)', value: newForm.formKey, onChange: handleKeyChange }),
                    errors.formKey && React.createElement("div", { style: { color: 'red', fontSize: '0.9em', marginTop: '0.25em' } }, errors.formKey),
                    React.createElement("div", { style: {
                            color: '#f57c00',
                            fontSize: '0.85em',
                            marginTop: '0.5em',
                            padding: '0.5em',
                            backgroundColor: '#fff3e0',
                            borderRadius: '4px',
                            borderLeft: '3px solid #ff9800'
                        } },
                        React.createElement("strong", null, "\u26A0\uFE0F Important:"),
                        " The key cannot be edited once the form is created. It is auto-generated from the label, but you can edit it manually.")),
                React.createElement(Form.Field, null,
                    React.createElement("label", null,
                        "Initial Prompt ",
                        React.createElement("span", { style: { color: '#9ca3af', fontWeight: 'normal' } }, "(optional)")),
                    React.createElement("textarea", { placeholder: "Describe the form you want to create and the AI agent will set it up for you...", value: promptText, onChange: (e) => setPromptText(e.target.value), rows: 3, style: {
                            width: '100%',
                            padding: '0.7em',
                            border: '1px solid rgba(34,36,38,.15)',
                            borderRadius: '4px',
                            fontFamily: 'inherit',
                            fontSize: '1em',
                            resize: 'vertical',
                        } }),
                    React.createElement("div", { style: { color: '#6b7280', fontSize: '0.85em', marginTop: '0.35em' } }, "This prompt will be sent to the AI agent when the form builder opens.")))),
        React.createElement(Modal.Actions, null,
            React.createElement(Button, { onClick: handleClose }, "Cancel"),
            React.createElement(Button, { primary: true, onClick: handleSave }, saveLabel))));
};
export default AddFormModal;
